// Type definitions for the game API

export interface Investigator {
  name: string;
  money: number;
  statistics?: InvestigatorStatistics;
  // Skills
  speed: number;
  speedMin: number;
  speedMax: number;
  sneak: number;
  sneakMin: number;
  sneakMax: number;
  fight: number;
  fightMin: number;
  fightMax: number;
  will: number;
  willMin: number;
  willMax: number;
  lore: number;
  loreMin: number;
  loreMax: number;
  luck: number;
  luckMin: number;
  luckMax: number;
  enteredGateId?: string;
  speedBreakdown?: SkillBreakdown;
  sneakBreakdown?: SkillBreakdown;
  fightBreakdown?: SkillBreakdown;
  willBreakdown?: SkillBreakdown;
  loreBreakdown?: SkillBreakdown;
  luckBreakdown?: SkillBreakdown;
  abilityDescription?: string;
  fixedPossessionsDescription?: string;
  randomPossessionsDescription?: string;
  hasDarkManDebt?: boolean;
  loanTaken?: boolean;
  canTakeLoan?: boolean;
  // Core stats
  focus: number;
  baseFocus?: number;
  focusBreakdown?: SkillBreakdown;
  evadeBreakdown?: SkillBreakdown;
  combatBreakdown?: SkillBreakdown;
  horrorBreakdown?: SkillBreakdown;
  spellBreakdown?: SkillBreakdown;
  movementPointsBreakdown?: SkillBreakdown;
  skillSliderPreviews?: SkillSliderPairPreview[];
  focusRemaining: number;
  skillSlidersIncreaseTogether?: boolean;
  stamina: number;
  staminaAboveMaximum: number;
  sanityAboveMaximum: number;
  maxStamina: number;
  baseMaxStamina?: number;
  maxStaminaBreakdown?: SkillBreakdown;
  sanity: number;
  maxSanity: number;
  baseMaxSanity?: number;
  maxSanityBreakdown?: SkillBreakdown;
  clues: number;
  maxClueTokens?: number | null;
  movementPoints: number;
  canNoLongerMoveThisTurn: boolean;
  startingLocation: string;
  commonItems?: CommonItem[];
  uniqueItems?: UniqueItem[];
  spells?: Spell[];
  skills?: Skill[];
  allies?: Ally[];
  specialCards?: SpecialCard[];
  traumas?: Injury[];
  madnesses?: Madness[];
  personalStory?: PersonalStory;
  relationship?: Relationship | null;
  partnerRelationship?: Relationship | null;
  corruptions?: Corruption[];
  monsterTrophies?: Monster[];
  gateTrophies?: Gate[];
  delayed: boolean;
  isDevoured?: boolean;
  requiresReplacementChoiceOnUpkeep?: boolean;
  explored?: boolean;
  hasBastToken?: boolean;
  eihortBroodTokens?: number;
}

export type Expansion = 'Base' | 'Dunwich' | 'CurseOfTheDarkPharaoh' | 'Kingsport' | 'TheKingInYellow' | 'TheBlackGoat' | 'TheLurkerAtTheThreshold' | 'Insmouth' | 'FanMadeInvestigators';

export interface ItemBase {
  name: string;
  title?: string;
  description?: string;
  expansion?: Expansion;
  usesRemaining?: number | null;
  maximumUsesRemaining?: number | null;
  exhausted?: boolean;
  used?: boolean;
  equipped?: boolean;
}

export interface CommonItem extends ItemBase {
  id?: string;
  price?: number;
  type?: string;
  hands?: number;
  taskLocations?: string[];
  completedTaskLocations?: string[];
  taskReward?: string;
}

export interface UniqueItem extends ItemBase {
  id?: string;
  price?: number;
  type?: string;
  hands?: number;
  missionReward?: string;
  missionSacrifice?: string;
  taskLocations?: string[];
  isExhibit?: boolean;
  completedTaskLocations?: string[];
  storedMonsters?: Monster[];
}

export interface SpecialCard extends ItemBase {
  universityAlumnus?: boolean;
  universityUpkeepTurn?: number | null;
  isNodensBlessing?: boolean;
  cluesNumber?: number;
  type?: string;
  hands?: number;
}

export type CorruptionCardType = 'Green' | 'Red';
export type CorruptionSignColor = 'White' | 'Black';

export interface Corruption extends ItemBase {
  id: string;
  cardType: CorruptionCardType;
  dimensionSign: string;
  signColor: CorruptionSignColor;
  textDescription?: string;
}

export interface Skill extends ItemBase {
}

export interface SkillModifierInfo {
  id?: string;
  source?: string;
  sourceType?: string | null;
  value: number;
}

export interface SkillBreakdown {
  baseSkillName?: string;
  base: number;
  total: number;
  modifiers?: SkillModifierInfo[];
}

export interface SkillSliderPairPreview {
  pair: string;
  positions: {
    sliderPosition: number;
    first: SkillBreakdown;
    second: SkillBreakdown;
    checks: Record<string, SkillBreakdown>;
    movementPointsBreakdown?: SkillBreakdown | null;
  }[];
}

export interface MonsterModifierInfo {
  id?: string;
  source?: string;
  sourceType?: string | null;
  value: number;
}

export interface MonsterStatBreakdown {
  base: number;
  total: number;
  modifiers?: MonsterModifierInfo[];
}

export interface Modification {
  id: string;
  additionalData?: string | null;
  staticValue?: number | null;
  textDescription?: string | null;
  color?: string | null;
  global?: boolean;
  used?: boolean;
}

export interface InvestigatorStatistics {
  cluesSpent: number;
  sanityDamageTaken: number;
  staminaDamageTaken: number;
  monstersDefeated: number;
  gatesClosed: number;
  gatesSealed: number;
  moneySpent: number;
  movementsMade: number;
  trainRides: number;
  knockouts: number;
  timesDrivenInsane: number;
  diceRolls: number;
  diceRollSuccesses: number;
  rollValueCounts: number[];
  damageToAncientOne: number;
}

export interface InvestigatorStatisticsSnapshot {
  investigatorName: string;
  statistics: InvestigatorStatistics;
}

export type AnyCard =
  | CommonItem
  | UniqueItem
  | Spell
  | Skill
  | Ally
  | SpecialCard
  | Injury
  | Madness
  | Corruption
  | AncientOne
  | Herald
  | Institution
  | Relationship;
export interface ActionCard {
  name?: string;
  [key: string]: unknown;
}

export interface DeckInfo {
  count: number;
  cards?: string[];
  fullCards?: AnyCard[];
  bottomCard?: AnyCard | null;
}

export interface CorruptionDeckInfo extends DeckInfo {
  topCardColor: CorruptionCardType | null;
}

export interface EncounterCardInfo {
  name: string;
  locationName: string;
  expansion?: string;
}

export interface EncounterDeckInfo {
  count: number;
  topCard?: EncounterCardInfo | null;
}

export interface DeckSummary {
  uniqueItems?: DeckInfo;
  exhibits?: DeckInfo;
  commonItems?: DeckInfo;
  spells?: DeckInfo;
  skills?: DeckInfo;
  allies?: DeckInfo;
  monsters?: DeckInfo;
  gates?: DeckInfo;
  madnesses?: DeckInfo;
  injuries?: DeckInfo;
  corruptions?: CorruptionDeckInfo;
  cultEncounters?: DeckInfo;
  innsmouthLook?: DeckInfo;
  encounters?: Record<string, EncounterDeckInfo>;
}

export interface GlobalModifierInfo {
  id: string;
  value: number;
  additionalData?: string | null;
  color?: string | null;
  textDescription?: string | null;
  sourceCard?: any;
  source?: string;
  sourceType?: string | null;
  used?: boolean;
}

export type SpellType = 'Upkeep' | 'BeforeCombat' | 'Combat' | 'UponTakingDamage' | 'Movement';

export interface Spell extends ItemBase {
  id?: string;
  sanityCost?: number;
  hands?: number;
  castingModifier?: number;
  castingModifierNote?: string;
  type?: SpellType;
}

// Game-state response pruning omits empty preview arrays; consumers must default them to [].
export interface SpellSanityCostBreakdown {
  base: number | null;
  adjustments?: SkillModifierInfo[];
  adjustedCost: number | null;
  automaticReductions?: SkillModifierInfo[];
  total: number | null;
  notes?: string[];
}

export interface SpellCastingPreview {
  currentCastingModifier?: number | null;
  castingModifierExplanation?: string | null;
  effectiveSanityCost: number | null;
  sanityCostBreakdown: SpellSanityCostBreakdown;
  castingBlocked: boolean;
  blockingSources?: string[];
}

export type SpellCastingPreviews = Record<string, Record<string, SpellCastingPreview>>;

export interface ItemPurchasePreview {
  printedPrice: number;
  offeredPrice: number;
  offerSource: string;
  normalizedPrice: number;
  frugalSource?: string | null;
  priceAfterFrugal: number;
  adjustments?: SkillModifierInfo[];
  total: number;
  notes?: string[];
}
export interface ItemWeaponPreview {
  printedType: string;
  effectiveType: string;
  conversionSources?: string[];
}
export type ItemCardPreviews = Record<string, Record<string, Record<string, ItemWeaponPreview>>>;

export interface Ally extends ItemBase {
}

export interface Injury extends ItemBase {
  similarMadnessName?: string;
}

export interface Madness extends ItemBase {
  similarInjuryName?: string;
}

export interface PersonalStory extends ItemBase {
  progress?: number;
  isReward?: boolean;
  failDescription?: string;
  rewardDescription?: string;
  failName?: string;
  rewardName?: string;
}

export interface Relationship extends ItemBase {
  id: string;
  partnerInvestigatorName?: string;
  partnerPlayerName?: string | null;
}

export interface MythosCard {
  id?: string;
  name: string;
  type?: string;
  processType?: string | null;
  shouldBeActiveForOneTurn?: boolean;
  textDescription?: string;
  rumorDescription?: string | null;
  rumorLocation?: string | null;
  rumorData?: any;
  rumorPassCondition?: string | null;
  rumorPassReward?: string | null;
  rumorFailCondition?: string | null;
  rumorFailPenalty?: string | null;
  activityLocationName?: string | null;
}

export interface Connection {
  to: string;
  color: 'Black' | 'White' | 'Both' | 'None';
}

export interface Gate {
  id?: string;
  otherWorld: string;
  dimensionSign: string;
  modifier: number;
  expansion?: Expansion;
  markerId?: string;
  ability?: 'None' | 'Moving' | 'Devouring' | 'Doom' | 'Endless' | 'Monstrous' | 'Blood' | 'Madness';
  secondaryOtherWorld?: string;
  secondaryDimensionSign?: string;
}

export interface Rift {
  id: number;
  dimensionalSign: string;
  color: string;
  turnsActive: number;
}

export interface Location {
  specialEncounterInfo?: {
    isAvailable: boolean;
    unavailableReason?: string;
    cards?: ({ kind: 'common'; card: CommonItem; remainingCount?: number }
      | { kind: 'special'; card: SpecialCard; remainingCount?: number })[];
  };
  encounter1?: string;
  encounter2?: string;
  aquatic?: boolean;
  isAquatic?: boolean;
  specialEncounterText?: string;
  name: string;
  district: string;
  city?: 'Arkham' | 'Dunwich' | 'Kingsport' | 'Innsmouth';
  train?: boolean;
  hasPatrol?: boolean;
  agentTokens?: number;
  hasExpedition?: boolean;
  hasAncientWhisperMarker?: boolean;
  cannotBeAccessedDirectly?: boolean;
  isStable: boolean;
  isSealed: boolean;
  isClosed: boolean;
  type: 'Location' | 'Street' | 'Special' | 'OtherWorld';
  connections?: Connection[];
  investigatorNames?: string[];
  cluesNumber: number;
  gates?: Gate[];
  monsters?: Monster[];
  rifts?: Rift[];
  placedCommonItems?: CommonItem[];
  placedUniqueItems?: UniqueItem[];
  facedownExhibitItemCount?: number;
}

export interface GameLogEntry {
  timestamp: string;
  message: string;
  playerName?: string | null;
  actionType?: string | null;
}

export interface PlayerSettings {
  autoResolveRolls: boolean;
}

export type InvestigatorDrawType = 'RandomDraft' | 'Random' | 'Selection';
export type AncientOneSelectionType = 'Random' | 'Selection';

export type GameStatistics = {
  elderSignUsed: number;
  missionsCompleted: number;
}

export type GameSettings = {
    expectedPlayersCount: number;
    investigatorsPerPlayer: number;
    maxInvestigatorsCount?: number | null;
    investigatorDrawType: string;
    ancientOneSelectionType: string;
    enableDunwichBoard?: boolean;
    enableKingsportBoard?: boolean;
    enableInnsmouthBoard?: boolean;
    includeDunwichCards: boolean;
    includeDarkPharaohCards?: boolean;
    includeKingsportCards?: boolean;
    includeKingInYellowCards?: boolean;
    includeBlackGoatCards?: boolean;
    includeMiskatonicCards?: boolean;
    includeLurkerCards?: boolean;
    includeInsmouthCards?: boolean;
    usePersonalStories?: boolean;
    useNewGateMarkers?: boolean;
    includeFanMadeInvestigators?: boolean;
    randomCluePlacement?: boolean;
    isPrivate: boolean;
}

export type SkillCheckInformation = {
    investigatorName?: string;
    playerName?: string;
    skillName?: string;
    baseSkillName?: string;
    monsterName?: string;
    modifier?: number;
    requiredSuccesses?: number;
    diceToRoll?: number;
    rolledDice?: number[];
    successThreshold?: number;
    successes?: number;
    bonusSuccessesFromSpendTwoClues?: number;
    isSucceeded?: boolean;
    isCombatCheck?: boolean;
    isGateClosingCheck?: boolean;
    stage?: string;
    modificationBreakdown: object;
    notes?: string[];
}

export type GateResult =
{
  result: string;
  location?: Location;
  gate?: Gate;
  isBurst: boolean;
  doomTokensAdded?: number;
}

export type ClueResult =
{
  result: string;
  location?: Location;
  blocked: boolean;
  investigator?: Investigator;
}

export type MonsterMovement = {
  dimensionSign: string;
  color: string;
}

export type RiftProgressTrack = {
  id: string;
  monsterMovements: MonsterMovement[];
  locations: string[];
}

export type RiftProgressEntry = {
  id: number;
  dimensionalSign: string;
  color: string;
  isOpen: boolean;
  tracks: RiftProgressTrack[];
}

export type MythosBreakdown = {
  name?: string;
  expansion?: Expansion;
  gate?: GateResult[];
  clues?: ClueResult[];
  appearedMonsters?: Monster[];
  movedMonsters?: Monster[];
  terrorLevelIncrease?: number;
  doomTrackIncrease?: number;
  monsterMovement?: MonsterMovement[];
  effect?: string;
  rumorPassCondition?: string | null;
  rumorPassReward?: string | null;
  rumorFailCondition?: string | null;
  rumorFailPenalty?: string | null;
}

export type FedRaid = {
  factoryDistrict: number;
  churchGreen: number;
  innsmouthShore: number;
};

export interface GameState {
  comparisonRevision?: number | null;
  activeInvestigatorName?: string | null;
  currentTurn?: number;
  latestMythosSequence?: {
    sequenceId: number;
    roundLabel: string;
    entries?: Array<{
      occurrenceId: string;
      cardId: string;
      cardName: string;
      cardType: string;
      status: 'Resolving' | 'Resolved' | 'Interrupted';
      results?: {doomChange: number; terrorChange: number; details?: MythosBreakdown | null; events?: string[] | null} | null;
    }> | null;
  } | null;
  gameId: string;
  status: string;
  currentPhase: string;
  expectedPlayersCount: number;
  investigatorsPerPlayer: number;
  maxInvestigatorsCount?: number | null;
  startingInvestigatorCount?: number | null;
  investigatorDrawType: InvestigatorDrawType;
  ancientOneSelectionType: AncientOneSelectionType;
  includeDunwichCards: boolean;
  players?: Player[];
  playersDetailed?: PlayerSummary[];
  gameSettings?: GameSettings;
  playersOrder?: string[];
  firstPlayer: string | null;
  firstInvestigator?: string | null;
  activePlayer: string | null;
  investigators?: Record<string, Investigator>;
  investigatorsByName?: Record<string, Investigator>;
  spellCastingPreviews?: SpellCastingPreviews;
  itemCardPreviews?: ItemCardPreviews;
  investigatorTurnOrder?: string[];
  owner?: string | null;
  ownerUserId?: string | null;
  locations?: Location[];
  gameLog?: GameLogEntry[];
  randomSeed: number;
  data?: Record<string, any>;
  createdAt: string;
  lastUpdatedAt: string | null;
  availableActions?: UserAction[];
  terrorLevel: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
  dunwichHorrorTerrorTrack?: 0 | 1 | 2 | 3;
  activeRumor?: MythosCard | null;
  activeProcess?: MythosCard | null;
  activeMythos?: MythosCard | null;
  mythosInformation?: string | null;
  mythosBreakdown?: string[];
  mythosDetails?: MythosBreakdown;
  encounterInformation?: string | null;
  damageInformation?: string | null;
  otherWorldEncounterInformation?: string | null;
  finalBattleInformation?: string | null;
  currentCombatMonsters?: Monster[];
  currentFightingMonster?: Monster | null;
  maximumGatesAllowed?: number;
  globalModifiers?: GlobalModifierInfo[];
  maximumMonstersInArkham?: number;
  kingInYellowAct?: number;
  outskirtsLimit?: number;
  decks?: DeckSummary;
  isFinalBattle?: boolean;
  investigatorStatistics?: Record<string, InvestigatorStatistics>;
  investigatorsStats?: InvestigatorStatisticsSnapshot[];
  statistics?: GameStatistics;
  skillCheckInformation?: SkillCheckInformation;
  ancientOne?: AncientOne;
  activeHeralds?: Herald[];
  activeGuardians?: Guardian[];
  activeInstitution?: Institution | null;
  riftProgress?: RiftProgressEntry[];
  hypnosVisionLocation?: string | null;
  dunwichHorrorCurrentRollInfo?: string;
  specialCardsInPlay?: SpecialCard[];
  ancientOneDamageDealt?: number;
  ancientOneDamageThreshold?: number | null;
  deepOnesRisingTrack?: number;
  fedsRaidInnsmouthTrack?: FedRaid;
  martialLawDeclared?: boolean;
}

export interface Player {
  name: string;
  joinedAt: string;
  settings?: PlayerSettings;
  controlledInvestigators?: string[];
  userId?: string | null;
}

export interface UserAction {
  shortLabel?: string | null;
  presentationMode?: 'Legacy' | 'NewActions' | null;
  trade?: {
    direction: 'give' | 'take' | 'finish';
    resource: 'item' | 'money' | 'clue' | 'menu';
    counterpartInvestigatorName?: string | null;
    itemKey?: string | null;
    amount?: number | null;
  } | null;
  spellCastingPreview?: SpellCastingPreview;
  itemPurchasePreview?: ItemPurchasePreview;
  id: string;
  name: string;
  description: string;
  playerName?: string;
  investigatorName?: string;
  investigator?: Pick<Investigator, 'name' | 'delayed' | 'isDevoured'> | null;
  cardType?: string | null;
  card?: AnyCard | Herald[] | number | null;
  locationName?: string | null;
  isLocationAction?: boolean;
  color?: string | null;
  disabled?: boolean | null;
  autoResolve?: boolean;
  sliderPosition?: number | null;
}

export interface MonsterCheckPreview {
  status: 'available' | 'notRequired' | 'special'; breakdown?: SkillBreakdown; note?: string;
  successThreshold?: number | null; requiredSuccesses?: number | null;
}
export interface MonsterComparisonChecks { combat: MonsterCheckPreview; horror: MonsterCheckPreview; evade?: MonsterCheckPreview | null; }
export interface MonsterAssumedCast { itemRef: string; successes: number; }
export interface MonsterComparisonRequest { revision: number; monsterRef: string; investigatorName: string; equippedRefs?: string[]; casts?: MonsterAssumedCast[]; }
export interface MonsterEquipmentPreview {
  reference: string; identity: string; name: string; kind: string; hands: number; equipped: boolean;
  exhausted: boolean; canEquip: boolean; canAssumeCast: boolean; activeSpell: boolean;
  combatBonuses: Array<{ type: string; value: number }>;
}
export interface MonsterComparisonResult {
  revision: number; monsterRef: string; investigatorName: string; checks: MonsterComparisonChecks;
  items: MonsterEquipmentPreview[]; equippedRefs: string[]; casts: MonsterAssumedCast[];
  occupiedHands: number; handCapacity: number; validationMessages: string[];
}
export class MonsterComparisonError extends Error {
  constructor(message: string, public status: number) { super(message); }
}
export interface Monster {
  id?: string;
  comparisonRef?: string;
  checkPreviewsByInvestigatorName?: Record<string, MonsterComparisonChecks>;
  name: string;
  expansion?: Expansion;
  combatRating: number;
  specialCombatRating?: boolean;
  SpecialCombatRating?: boolean;
  combatDamage: number;
  specialCombatDamage?: boolean;
  SpecialCombatDamage?: boolean;
  toughness: number;
  Toughness?: number;
  horrorRating: number;
  horrorDamage: number;
  specialHorrorDamage?: boolean;
  SpecialHorrorDamage?: boolean;
  awareness: number;
  movementType: 'Normal' | 'Fast' | 'Stationary' | 'Flying' | 'Special' | 'Stalker' | 'Aquatic';
  effectiveMovementType?: Monster['movementType'];
  dimensionSign: 'Crescent' | 'Square' | 'Circle' | 'Diamond' | 'Hexagon' | 'Slash' | 'Star' | 'Triangle';
  toughnessBreakdown?: MonsterStatBreakdown;
  type: 'None' | 'Spawn';
  abilities?: string[];
  Abilities?: string[];
  suppressedAbilities?: string[];
  SuppressedAbilities?: string[];
  additionalInfo?: string;
  AdditionalInfo?: string;
  endlessBreakdown?: MonsterStatBreakdown;
  combatRatingBreakdown?: MonsterStatBreakdown;
  combatDamageBreakdown?: MonsterStatBreakdown;
  horrorRatingBreakdown?: MonsterStatBreakdown;
  horrorDamageBreakdown?: MonsterStatBreakdown;
  awarenessBreakdown?: MonsterStatBreakdown;
}

export interface ZharSpecialData {
  firstDefeatOccurred: boolean;
}

export interface RhanTegothSpecialData {
  cultists: Monster[];
}

export interface BokrugSpecialData {
  beingsOfIb: BeingOfIb[];
  clueCount: number;
}

export interface BeingOfIb extends Monster {
  id: string;
}

export interface GhatanothoaSpecialData {
  revealedCount: number;
  revealedTokens: boolean[];
  attackMovementCost: number;
}

export interface AncientOne {
  name: string;
  expansion?: Expansion;
  used?: boolean;
  exhausted?: boolean;
  worshippersDescription: string;
  powerDescription: string;
  attackDescription: string;
  startOfBattleDescription?: string | null;
  modifier: number;
  combatModifications?: Modification[];
  doomTrack: number;
  maximumDoomTrack: number;
  specialData: any;
}

export interface Herald {
  name: string;
  used?: boolean;
  exhausted?: boolean;
  modifications?: Modification[];
}

export interface Guardian {
  name: string;
  used?: boolean;
  exhausted?: boolean;
  modifications?: Modification[];
}

export interface Institution {
  id: string;
  name: string;
  used?: boolean;
  exhausted?: boolean;
  subtype: string;
  setupDescription: string;
  rules: { name: string; description: string }[];
  bureauState?: { availableAgents: number; custodyAllies: Partial<Record<'Dunwich' | 'Kingsport' | 'Innsmouth', Ally>> } | null;
  modifications?: Modification[];
}

export interface ActionsResponse {
  actions: UserAction[];
}

export interface GameLogResponse {
  gameLog: GameLogEntry[];
}

export interface JoinGameRequest {
  playerName: string;
  gameId?: string;
}

export interface JoinGameResponse {
  message?: string;
  gameId?: string;
  playersCount?: number;
  expectedPlayers?: number;
  status?: string;
}

export interface StartGameResponse {
  maxInvestigatorsCount?: number | null;
  enableDunwichBoard?: boolean;
  enableKingsportBoard?: boolean;
  enableInnsmouthBoard?: boolean;
  message: string;
  gameId: string;
  randomSeed: number;
  expectedPlayers: number;
  investigatorsPerPlayer: number;
  investigatorDrawType: InvestigatorDrawType;
  ancientOneSelectionType: AncientOneSelectionType;
  includeDunwichCards: boolean;
  includeDarkPharaohCards?: boolean;
  includeKingsportCards?: boolean;
  includeKingInYellowCards?: boolean;
  includeBlackGoatCards?: boolean;
  includeMiskatonicCards?: boolean;
  includeLurkerCards?: boolean;
  includeInsmouthCards?: boolean;
  usePersonalStories?: boolean;
  useNewGateMarkers?: boolean;
  includeFanMadeInvestigators?: boolean;
  randomCluePlacement?: boolean;
  owner: string | null;
  ownerUserId?: string | null;
  isPrivate: boolean;
}

export interface PlayerSummary {
  name: string;
  userId?: string | null;
  isOnline?: boolean;
}

export interface GameSummary {
  gameId: string;
  status: string;
  result?: string | null;
  currentPhase: string;
  expectedPlayersCount: number;
  playersCount: number;
  players: string[];
  createdAt: string;
  lastUpdatedAt?: string | null;
  lastActionAt?: string | null;
  firstPlayer?: string | null;
  activePlayer?: string | null;
  owner?: string | null;
  ownerUserId?: string | null;
  isCurrent: boolean;
}

export interface PagedGamesResponse {
  games: GameSummary[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export type AdminGrantItemType = 'CommonItem' | 'UniqueItem' | 'Spell' | 'Skill' | 'Ally' | 'AllyFromBox' | 'Monster' | 'Exhibit' | 'GateTrophy' | 'Corruption';
export type AdminGrantType = AdminGrantItemType | 'Money' | 'Clues';

export interface AdminGrantEntry {
  deckIndex: number;
  label: string;
}

export interface AdminGrantOptions {
  gameId: string;
  stateRevision: number;
  investigators: string[];
  items: Record<AdminGrantItemType, AdminGrantEntry[]>;
}

export interface AdminGrantRequest {
  investigatorName: string;
  expectedStateRevision: number;
  type: AdminGrantType;
  deckIndex?: number;
  amount?: number;
}

export class AdminGrantError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = 'AdminGrantError';
  }
}

export type AdminDeckType = 'Mythos' | 'ArkhamEncounter' | 'OtherWorldEncounter';
export type AdminDeckDirection = 'Up' | 'Down' | 'Top';
export interface AdminDeckOptions {
  gameId: string;
  arkhamLocations: string[];
  otherWorldLocations: string[];
}
export interface AdminDeckEntry {
  deckIndex: number;
  id: string;
  expansion: string;
  text?: string | null;
  color?: Color | null;
  mythosDetails?: MythosBreakdown | null;
}
export interface AdminDeckResponse {
  gameId: string;
  stateRevision: number;
  deckType: AdminDeckType;
  location: string | null;
  cards: AdminDeckEntry[];
}
export interface AdminDeckMoveRequest {
  expectedStateRevision: number;
  deckIndex: number;
  direction: AdminDeckDirection;
  location?: string;
}
export class AdminDeckError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = 'AdminDeckError';
  }
}

export interface AdminUndoActionsResponse {
  message: string;
  gameId: string;
  undoneCount: number;
  remainingActions: number;
}

export interface AdminClearNotificationMessageIdsResponse {
  message: string;
  gameId: string;
  playersCount: number;
  clearedCount: number;
}

export interface AdminGameFullStateResponse {
  gameId: string;
  fullStateJson?: string | null;
  fullStateActionCounter?: number | null;
  status: string;
  createdAt: string;
  savedAt: string;
  isPrivate: boolean;
  activePlayer?: string | null;
}

export interface AdminUpdateGameFullStateResponse {
  message: string;
  gameId: string;
}

export type StateRequestClassification = 'Unclassified' | 'Cold' | 'Warm' | 'FinishedSnapshot';
export type StateRequestOutcome = 'Success' | 'NotFound' | 'Rejected' | 'Cancelled' | 'Failed';

export interface StatePerformanceMetric {
  count: number;
  totalMilliseconds: number;
  averageMilliseconds: number;
  maximumMilliseconds: number;
  p50Milliseconds?: number;
  p95Milliseconds?: number;
  p99Milliseconds?: number;
}

export interface StatePerformanceStepMetric extends StatePerformanceMetric {
  name: string;
}

export interface StatePerformanceStepTiming {
  name: string;
  durationMilliseconds: number;
}

export interface StatePerformanceAggregate {
  classification: string;
  requestCount: number;
  outcomeCounts: Record<string, number>;
  totalDuration: StatePerformanceMetric;
  stages: StatePerformanceStepMetric[];
  details: StatePerformanceStepMetric[];
}

export interface StatePerformanceRecentRequest {
  startedAtUtc: string;
  gameId?: string | null;
  classification: StateRequestClassification;
  outcome: StateRequestOutcome;
  statusCode?: number | null;
  totalDurationMilliseconds: number;
  slowestStage?: string | null;
  slowestStageMilliseconds?: number | null;
  stages: StatePerformanceStepTiming[];
  details: StatePerformanceStepTiming[];
  concurrentRequests?: number;
  dimensions?: Record<string, string> | null;
}

export interface StatePerformanceSummary {
  requestCount: number;
  successCount: number;
  notFoundCount: number;
  rejectedCount: number;
  failedCount: number;
  cancelledCount: number;
  coldRequestCount: number;
  warmRequestCount: number;
  finishedSnapshotRequestCount: number;
  unclassifiedRequestCount: number;
}

export interface StatePerformanceWork {
  workId: string;
  kind: string;
  gameId: string;
  startedAtUtc: string;
  outcome: StateRequestOutcome;
  totalDurationMilliseconds: number;
  details: StatePerformanceStepTiming[];
  dimensions: Record<string, string>;
}

export interface StatePerformanceSnapshot {
  serverStartedAtUtc: string;
  generatedAtUtc: string;
  recentRequestLimit: number;
  summary: StatePerformanceSummary;
  aggregates: StatePerformanceAggregate[];
  recentRequests: StatePerformanceRecentRequest[];
  workAggregates?: StatePerformanceAggregate[];
  recentSlowWork?: StatePerformanceWork[];
}

export interface AdminDatabaseUser {
  id: string;
  userName: string;
  passwordHash: string;
  passwordSalt: string;
  role: UserRole;
  isGuest: boolean;
  language: UserLanguage;
  sendNotificationsToTelegram: boolean;
  sendNotificationsToEmail: boolean;
  telegramId?: string | null;
  email?: string | null;
  emailConfirmationStatus?: string | null;
  notificationMessageId?: string | null;
  emailNotificationMessageId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminDatabaseRefreshToken {
  id: string;
  token: string;
  expiresAt: string;
  revokedAt?: string | null;
  userId: string;
}

export interface AdminDatabaseGamePlayer {
  id: string;
  gameId: string;
  userId: string;
  playerName: string;
  joinedAt: string;
  autoResolveRolls: boolean;
}

export interface AdminDatabaseGameSnapshot {
  gameId: string;
  fullStateJson: string;
  fullStateActionCounter: number;
  replayTailActionsJson: string;
  finishedStateJson?: string | null;
  status: string;
  savedAt: string;
  isPrivate: boolean;
  activePlayer?: string | null;
}

export interface AdminDatabaseAchievement {
  id: number;
  name: string;
  description: string;
  group: string;
}

export interface AdminDatabasePlayerAchievement {
  id: number;
  userId: string;
  achievementId: number;
  gameId: string;
  receivedAt: string;
}

export interface AdminDatabaseExportPayload {
  schemaVersion: number;
  exportedAtUtc: string;
  users: AdminDatabaseUser[];
  refreshTokens: AdminDatabaseRefreshToken[];
  gamePlayers: AdminDatabaseGamePlayer[];
  gameSnapshots: AdminDatabaseGameSnapshot[];
  achievements: AdminDatabaseAchievement[];
  playerAchievements: AdminDatabasePlayerAchievement[];
}

export interface AdminDatabaseImportResponse {
  message: string;
  usersCount: number;
  refreshTokensCount: number;
  gamePlayersCount: number;
  gameSnapshotsCount: number;
  achievementsCount: number;
  playerAchievementsCount: number;
}

export type WikiDeckType =
  | 'CommonItems'
  | 'UniqueItems'
  | 'Exhibits'
  | 'Spells'
  | 'Skills'
  | 'Allies'
  | 'Special'
  | 'Injuries'
  | 'Madness'
  | 'Corruptions'
  | 'Relationships'
  | 'Gates'
  | 'Contact'
  | 'Investigator'
  | 'OtherWorld'
  | 'Monsters'
  | 'AncientOnes'
  | 'Heralds'
  | 'Institutions'
  | 'Guardians'
  | 'Mythos'
  | 'CultEncounters'
  | 'InnsmouthLook';

export type Color = 'Blue' | 'Green' | 'Yellow' | 'Red' | 'White';
export type OtherWorldContactResolver = {
  description: string;
}

export type OtherWorldContact = {
  color: Color;
  resolvers: Record<string, OtherWorldContactResolver>;
}

export type ExhibitEncounterCard = {
  name: string;
  description: string;
  newAncientWhisperMarkerLocation: string;
}

export type CultEncounter = {
  id: string;
  name: string;
  description: string;
}

export type InnsmouthLookCard = {
  id: string;
  textDescription: string;
}

export type WikiDeckResponse = ({
  deckType: Exclude<
    WikiDeckType,
    'OtherWorld' | 'Investigator' | 'Monsters' | 'AncientOnes' | 'Heralds' | 'Guardians' | 'Institutions' | 'Gates' | 'Mythos' | 'Corruptions' | 'CultEncounters' | 'InnsmouthLook'
  >;
  language: UserLanguage;
  cards: AnyCard[];
  encounterCards?: ExhibitEncounterCard[];
} | {
  deckType: 'Corruptions';
  language: UserLanguage;
  cards: Corruption[];
} | {
  deckType: 'CultEncounters';
  language: UserLanguage;
  cards: CultEncounter[];
} | {
  deckType: 'InnsmouthLook';
  language: UserLanguage;
  cards: InnsmouthLookCard[];
} | {
  deckType: 'Gates';
  language: UserLanguage;
  cards: Gate[];
} | {
  deckType: 'Mythos';
  language: UserLanguage;
  cards: MythosBreakdown[];
} | {
  deckType: 'OtherWorld';
  language: UserLanguage;
  cards: OtherWorldContact[];
}  | {
  deckType: 'Investigator';
  language: UserLanguage;
  cards: Investigator[];
} | {
  deckType: 'Monsters';
  language: UserLanguage;
  cards: Monster[];
} | {
  deckType: 'AncientOnes';
  language: UserLanguage;
  cards: AncientOne[];
} | {
  deckType: 'Heralds';
  language: UserLanguage;
  cards: Herald[];
} | {
  deckType: 'Institutions';
  language: UserLanguage;
  cards: Institution[];
} | {
  deckType: 'Guardians';
  language: UserLanguage;
  cards: Guardian[];
}) & {
  includeBaseCards?: boolean;
  includeDunwichCards?: boolean;
  includeDarkPharaohCards?: boolean;
  includeKingsportCards?: boolean;
  includeKingInYellowCards?: boolean;
  includeBlackGoatCards?: boolean;
  includeMiskatonicCards?: boolean;
  includeLurkerCards?: boolean;
  includeInsmouthCards?: boolean;
  includeFanMadeInvestigators?: boolean;
}

export type UserRole = 'User' | 'Admin';
export type UserLanguage = 'En' | 'Ru';
export type MonsterTokenStyle = 'Classic' | 'Digital';
export type MapStyle = 'Classic' | 'Reworked';

export interface UserSettings {
  language: UserLanguage;
  monsterTokenStyle: MonsterTokenStyle;
  /** Independent map renderer preference; Reworked is displayed as Digital. */
  mapStyle?: MapStyle;
  otherWorldsBelowMap?: boolean;
  sendNotificationsToTelegram: boolean;
  telegramId?: string | null;
  sendNotificationsToEmail: boolean;
  email?: string | null;
}

export interface PlayerAchievementProgress {
  name: string;
  description: string;
  group: string;
  isReceived: boolean;
  gameId?: string | null;
  receivedAt?: string | null;
}

export interface PlayerAchievementInGame {
  name: string;
  description: string;
  group: string;
  gameId: string;
  receivedAt: string;
}

export interface UserInfo {
  id: string;
  userName: string;
  role: UserRole;
  createdAt: string;
  updatedAt: string;
}

export interface SignupSettings {
  language: UserLanguage;
  sendNotificationsToTelegram: boolean;
  telegramId?: string | null;
  sendNotificationsToEmail: boolean;
  email?: string | null;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  userName: string;
  role: UserRole;
  language: UserLanguage;
  monsterTokenStyle?: MonsterTokenStyle;
  /** Independent map renderer preference; Reworked is displayed as Digital. */
  mapStyle?: MapStyle;
  otherWorldsBelowMap?: boolean;
}

declare global {
  interface Window {
    __ARKHAM_CONFIG__?: {
      API_BASE_URL?: string;
    };
  }
}

const AUTH_TOKEN_KEY = 'boardgame_auth_token';
const AUTH_REFRESH_TOKEN_KEY = 'boardgame_auth_refresh';
const GUEST_ID_KEY = 'boardgame_guest_id';
const GUEST_FLAG_HEADER = 'X-Is-Guest';
const GUEST_ID_HEADER = 'X-Guest-Id';
const DEFAULT_LOCAL_API_BASE_URL = 'http://localhost:5001/api';

function normalizeApiBaseUrl(baseUrl: string): string {
  return baseUrl.trim().replace(/\/+$/, '');
}

function resolveApiBaseUrl(): string {
  const runtimeBaseUrl = typeof window !== 'undefined'
    ? window.__ARKHAM_CONFIG__?.API_BASE_URL
    : undefined;
  const buildTimeBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const resolvedBaseUrl = runtimeBaseUrl || buildTimeBaseUrl || DEFAULT_LOCAL_API_BASE_URL;
  return normalizeApiBaseUrl(resolvedBaseUrl);
}

const API_BASE_URL = resolveApiBaseUrl();
let authToken: string | null = localStorage.getItem(AUTH_TOKEN_KEY);
let refreshToken: string | null = localStorage.getItem(AUTH_REFRESH_TOKEN_KEY);
let guestUserId: string | null = localStorage.getItem(GUEST_ID_KEY);
let onAuthUpdated: ((auth: AuthResponse | null) => void) | null = null;
const API_VERSION = '/v2';
const gameStateEtags = new Map<string, string>();
const gameStateResponses = new Map<string, GameState>();

const clearGameStateResponseCache = () => {
  gameStateEtags.clear();
  gameStateResponses.clear();
};

const normalizeInvestigator = (investigator: Investigator): Investigator => ({
  ...investigator,
  commonItems: investigator.commonItems ?? [],
  uniqueItems: investigator.uniqueItems ?? [],
  spells: investigator.spells ?? [],
  skills: investigator.skills ?? [],
  allies: investigator.allies ?? [],
  specialCards: investigator.specialCards ?? [],
  traumas: investigator.traumas ?? [],
  madnesses: investigator.madnesses ?? [],
  corruptions: investigator.corruptions ?? [],
  monsterTrophies: investigator.monsterTrophies ?? [],
  gateTrophies: investigator.gateTrophies ?? [],
});

const normalizeInvestigatorRecord = (
  investigators?: Record<string, Investigator>
): Record<string, Investigator> | undefined => {
  if (!investigators) {
    return undefined;
  }

  return Object.entries(investigators).reduce<Record<string, Investigator>>(
    (normalized, [name, investigator]) => {
      normalized[name] = normalizeInvestigator(investigator);
      return normalized;
    },
    {}
  );
};

const normalizeGameState = (state: GameState): GameState => {
  const investigatorsByName = normalizeInvestigatorRecord(state.investigatorsByName) ?? {};

  return {
    ...state,
    players: state.players ?? [],
    playersOrder: state.playersOrder ?? [],
    investigatorsByName,
    locations: (state.locations ?? []).map(location => ({
      ...location,
      connections: location.connections ?? [],
      investigatorNames: location.investigatorNames ?? [],
      gates: location.gates ?? [],
      monsters: location.monsters ?? [],
    })),
    gameLog: state.gameLog ?? [],
    data: state.data ?? {},
    availableActions: state.availableActions ?? [],
    activeHeralds: state.activeHeralds ?? [],
  };
};

const applyIdentityHeaders = (headers: Headers) => {
  if (authToken) {
    headers.set(GUEST_FLAG_HEADER, 'false');
    headers.delete(GUEST_ID_HEADER);
    return;
  }

  const normalizedGuestId = guestUserId?.trim();
  if (normalizedGuestId) {
    headers.set(GUEST_FLAG_HEADER, 'true');
    headers.set(GUEST_ID_HEADER, normalizedGuestId);
    return;
  }

  headers.set(GUEST_FLAG_HEADER, 'false');
  headers.delete(GUEST_ID_HEADER);
};

export const gameApi = {
  setAuthTokens(token: string | null, refresh: string | null, onUpdate?: (auth: AuthResponse | null) => void) {
    clearGameStateResponseCache();
    authToken = token;
    refreshToken = refresh;
    onAuthUpdated = onUpdate ?? null;
  },

  setGuestUserId(guestId: string | null) {
    const normalizedGuestId = guestId?.trim() || null;
    if (guestUserId !== normalizedGuestId) {
      clearGameStateResponseCache();
    }
    guestUserId = normalizedGuestId;
    if (guestUserId) {
      localStorage.setItem(GUEST_ID_KEY, guestUserId);
    } else {
      localStorage.removeItem(GUEST_ID_KEY);
    }
  },

  getGuestUserId() {
    return guestUserId;
  },

  authorizedFetch(input: RequestInfo | URL, init?: RequestInit) {
    const headers = new Headers(init?.headers || {});
    applyIdentityHeaders(headers);
    if (authToken) {
      headers.set('Authorization', `Bearer ${authToken}`);
    }
    return fetch(input, { ...init, headers }).then(async response => {
      if (response.status !== 401 || !refreshToken) {
        return response;
      }

      // Attempt refresh once
      try {
        const refreshHeaders = new Headers({ 'Content-Type': 'application/json' });
        applyIdentityHeaders(refreshHeaders);
        const refreshResponse = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: refreshHeaders,
          body: JSON.stringify({ refreshToken })
        });
        if (!refreshResponse.ok) {
          onAuthUpdated?.(null);
          return response;
        }
        const auth = (await refreshResponse.json()) as AuthResponse;
        authToken = auth.accessToken;
        refreshToken = auth.refreshToken;
        onAuthUpdated?.(auth);

        const retryHeaders = new Headers(init?.headers || {});
        applyIdentityHeaders(retryHeaders);
        retryHeaders.set('Authorization', `Bearer ${authToken}`);
        return fetch(input, { ...init, headers: retryHeaders });
      } catch {
        onAuthUpdated?.(null);
        return response;
      }
    });
  },

  async signup(
    userName: string,
    password: string,
    settings?: SignupSettings,
    currentGuestUserId?: string | null
  ): Promise<AuthResponse> {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    applyIdentityHeaders(headers);
    const response = await fetch(`${API_BASE_URL}/auth/signup`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        userName,
        password,
        language: settings?.language,
        sendNotificationsToTelegram: settings?.sendNotificationsToTelegram,
        telegramId: settings?.telegramId,
        sendNotificationsToEmail: settings?.sendNotificationsToEmail,
        email: settings?.email,
        guestUserId: currentGuestUserId ?? undefined,
      }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to sign up');
    }
    return response.json();
  },

  async login(userName: string, password: string): Promise<AuthResponse> {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    applyIdentityHeaders(headers);
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ userName, password }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to login');
    }
    return response.json();
  },

  async logout(refreshToken: string): Promise<void> {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    applyIdentityHeaders(headers);
    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ refreshToken }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to logout');
    }
  },

  async refresh(refreshToken: string): Promise<AuthResponse> {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    applyIdentityHeaders(headers);
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ refreshToken }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to refresh token');
    }
    return response.json();
  },

  // Join the game
  async joinGame(playerName: string, gameId?: string): Promise<JoinGameResponse> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/game/join`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ playerName, gameId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to join game');
    }
    return response.json();
  },

  async updatePlayerSettings(settings: UserSettings): Promise<UserSettings> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/player/settings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        language: settings.language,
        monsterTokenStyle: settings.monsterTokenStyle,
        mapStyle: settings.mapStyle,
        otherWorldsBelowMap: settings.otherWorldsBelowMap,
        sendNotificationsToTelegram: settings.sendNotificationsToTelegram,
        telegramId: settings.telegramId,
        sendNotificationsToEmail: settings.sendNotificationsToEmail,
        email: settings.email,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update player settings');
    }

    return response.json();
  },

  async getPlayerSettings(playerName?: string, gameId?: string): Promise<UserSettings> {
    const params = new URLSearchParams();
    if (playerName) {
      params.append('playerName', playerName);
    }
    if (gameId) {
      params.append('gameId', gameId);
    }
    const url = params.toString()
      ? `${API_BASE_URL}/player/settings?${params.toString()}`
      : `${API_BASE_URL}/player/settings`;

    const response = await gameApi.authorizedFetch(url);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to load player settings');
    }
    return response.json();
  },

  async getPlayerAchievements(): Promise<PlayerAchievementProgress[]> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/player/achievements`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to load player achievements');
    }
    return response.json();
  },

  async getPlayerAchievementsByGame(playerName: string, gameId: string): Promise<PlayerAchievementInGame[]> {
    const params = new URLSearchParams();
    params.append('playerName', playerName);
    params.append('gameId', gameId);
    const response = await gameApi.authorizedFetch(
      `${API_BASE_URL}/player/achievements/game?${params.toString()}`
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to load achievements earned in game');
    }
    return response.json();
  },

  // Start a new game
  async startGame(
    seed?: number,
    expectedPlayersCount?: number,
    investigatorsPerPlayer?: number,
    investigatorDrawType?: InvestigatorDrawType,
    ancientOneSelectionType?: AncientOneSelectionType,
    includeDunwichCards?: boolean,
    playerName?: string,
    isPrivate?: boolean,
    includeDarkPharaohCards?: boolean,
    includeKingsportCards?: boolean,
    randomCluePlacement?: boolean,
    includeFanMadeInvestigators?: boolean,
    includeKingInYellowCards?: boolean,
    includeBlackGoatCards?: boolean,
    includeInsmouthCards?: boolean,
    usePersonalStories?: boolean,
    includeLurkerCards?: boolean,
    useNewGateMarkers?: boolean,
    includeMiskatonicCards: boolean = false,
    enableDunwichBoard: boolean = true,
    enableKingsportBoard: boolean = true,
    enableInnsmouthBoard: boolean = true,
    maxInvestigatorsCount?: number
  ): Promise<StartGameResponse> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/game/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        seed,
        expectedPlayersCount,
        investigatorsPerPlayer,
        maxInvestigatorsCount,
        investigatorDrawType,
        ancientOneSelectionType,
        includeDunwichCards,
        includeDarkPharaohCards,
        includeKingsportCards,
        includeKingInYellowCards,
        includeBlackGoatCards,
        includeMiskatonicCards,
        enableDunwichBoard,
        enableKingsportBoard,
        enableInnsmouthBoard,
        includeLurkerCards,
        includeInsmouthCards,
        usePersonalStories,
        useNewGateMarkers,
        includeFanMadeInvestigators,
        randomCluePlacement,
        playerName,
        isPrivate,
      }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to start game');
    }
    return response.json();
  },

  async getMonsterComparison(playerName: string | undefined, gameId: string,
    request: MonsterComparisonRequest, signal?: AbortSignal): Promise<MonsterComparisonResult> {
    const params = new URLSearchParams({ gameId, isTabActive: 'false', monsterComparison: JSON.stringify(request) });
    if (playerName) params.set('playerName', playerName);
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}${API_VERSION}/game/state?${params}`, { signal, cache: 'no-store' });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new MonsterComparisonError(error.message || 'The comparison could not be calculated.', response.status);
    }
    return response.json();
  },

  // Get current game state with available actions for a player (v2 stateless replay)
  async getGameState(playerName?: string, gameId?: string, signal?: AbortSignal): Promise<GameState | null> {
    const params = new URLSearchParams();
    if (playerName) {
      params.append('playerName', playerName);
    }
    if (gameId) {
      params.append('gameId', gameId);
    }
    const tabIsVisible = typeof document !== 'undefined'
      ? document.visibilityState === 'visible'
      : true;
    params.append('isTabActive', String(tabIsVisible));
    const urlParams = params.toString();
    const url = urlParams.length > 0
      ? `${API_BASE_URL}${API_VERSION}/game/state?${urlParams}`
      : `${API_BASE_URL}${API_VERSION}/game/state`;
    
    const headers = new Headers();
    const cachedEtag = gameStateEtags.get(url);
    if (cachedEtag && gameStateResponses.has(url)) {
      headers.set('If-None-Match', cachedEtag);
    }
    const response = await gameApi.authorizedFetch(url, { signal, headers });
    if (response.status === 304) {
      return gameStateResponses.get(url) ?? null;
    }
    if (response.status === 404) {
      gameStateEtags.delete(url);
      gameStateResponses.delete(url);
      return null; // No game exists
    }
    if (!response.ok) {
      throw new Error('Failed to get game state');
    }
    const state = normalizeGameState((await response.json()) as GameState);
    const etag = response.headers?.get?.('ETag');
    if (etag) {
      gameStateEtags.set(url, etag);
      gameStateResponses.set(url, state);
    }
    return state;
  },

  // Get full game log (v2 stateless replay)
  async getGameLog(playerName?: string, gameId?: string, signal?: AbortSignal): Promise<GameLogEntry[]> {
    const params = new URLSearchParams();
    if (playerName) {
      params.append('playerName', playerName);
    }
    if (gameId) {
      params.append('gameId', gameId);
    }
    const tabIsVisible = typeof document !== 'undefined'
      ? document.visibilityState === 'visible'
      : true;
    params.append('isTabActive', String(tabIsVisible));
    const urlParams = params.toString();
    const url = urlParams.length > 0
      ? `${API_BASE_URL}${API_VERSION}/game/log?${urlParams}`
      : `${API_BASE_URL}${API_VERSION}/game/log`;

    const response = await gameApi.authorizedFetch(url, { signal });
    if (response.status === 404) {
      return [];
    }
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get game log');
    }

    const data = (await response.json()) as GameLogResponse;
    return data.gameLog ?? [];
  },

  // Execute an action
  async executeAction(actionId: string, gameId?: string, playerName?: string): Promise<GameState> {
    const params = new URLSearchParams();
    if (gameId) {
      params.append('gameId', gameId);
    }
    if (playerName) {
      params.append('playerName', playerName);
    }

    const encodedActionId = encodeURIComponent(actionId);
    const query = params.toString();
    const url = query.length > 0
      ? `${API_BASE_URL}${API_VERSION}/game/action/${encodedActionId}?${query}`
      : `${API_BASE_URL}${API_VERSION}/game/action/${encodedActionId}`;
    const response = await gameApi.authorizedFetch(url, {
      method: 'POST',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to execute action');
    }
    const state = (await response.json()) as GameState;
    return normalizeGameState(state);
  },

  async listGames(playerName?: string, signal?: AbortSignal): Promise<GameSummary[]> {
    const params = new URLSearchParams();
    if (playerName) {
      params.append('playerName', playerName);
    }
    const query = params.toString();
    const url = query ? `${API_BASE_URL}${API_VERSION}/games?${query}` : `${API_BASE_URL}${API_VERSION}/games`;
    const response = await gameApi.authorizedFetch(url, { signal });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to list games');
    }
    const data = await response.json();
    return data.games ?? [];
  },

  async listFinishedGames(playerName?: string, signal?: AbortSignal): Promise<GameSummary[]> {
    const params = new URLSearchParams();
    if (playerName) {
      params.append('playerName', playerName);
    }
    const query = params.toString();
    const url = query
      ? `${API_BASE_URL}${API_VERSION}/games/finished?${query}`
      : `${API_BASE_URL}${API_VERSION}/games/finished`;
    const response = await gameApi.authorizedFetch(url, { signal });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to list finished games');
    }
    const data = await response.json();
    return data.games ?? [];
  },

  async listAllGames(): Promise<GameSummary[]> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/games/all`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to list all games');
    }
    const data = await response.json();
    return data.games ?? [];
  },

  async listAllFinishedGames(page: number = 1, owner?: string): Promise<PagedGamesResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    if (owner?.trim()) {
      params.append('owner', owner.trim());
    }
    const response = await gameApi.authorizedFetch(
      `${API_BASE_URL}/games/all/finished?${params.toString()}`
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to list all finished games');
    }
    const data = (await response.json()) as PagedGamesResponse;
    return {
      games: data.games ?? [],
      page: data.page,
      pageSize: data.pageSize,
      totalCount: data.totalCount,
      totalPages: data.totalPages,
    };
  },

  async selectCurrentGame(gameId: string): Promise<void> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/games/${encodeURIComponent(gameId)}/select`, {
      method: 'POST',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to select game');
    }
  },

  async getAdminGrantOptions(gameId: string): Promise<AdminGrantOptions> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/admin/games/${encodeURIComponent(gameId)}/grant-options`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to load grant options');
    }
    return response.json();
  },

  async getAdminDeckOptions(gameId: string): Promise<AdminDeckOptions> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/admin/games/${encodeURIComponent(gameId)}/decks/options`);
    if (!response.ok) {
      const error = await response.json();
      throw new AdminDeckError(error.message || 'Failed to load deck locations', response.status);
    }
    return response.json();
  },

  async getAdminDeck(gameId: string, type: AdminDeckType, location?: string): Promise<AdminDeckResponse> {
    const params = new URLSearchParams();
    if (location) params.set('location', location);
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/admin/games/${encodeURIComponent(gameId)}/decks/${type}?${params}`);
    if (!response.ok) {
      const error = await response.json();
      throw new AdminDeckError(error.message || 'Failed to load deck', response.status);
    }
    return response.json();
  },

  async moveAdminDeckCard(gameId: string, type: AdminDeckType, request: AdminDeckMoveRequest): Promise<{ gameId: string; message: string }> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/admin/games/${encodeURIComponent(gameId)}/decks/${type}/move`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new AdminDeckError(error.message || 'Failed to move card', response.status);
    }
    return response.json();
  },

  async getWikiDeck(
    type: WikiDeckType,
    language: UserLanguage,
    includeBaseCards: boolean = true,
    includeDunwichCards: boolean = true,
    includeDarkPharaohCards: boolean = true,
    includeKingsportCards: boolean = true,
    includeFanMadeInvestigators: boolean = true,
    includeKingInYellowCards: boolean = true,
    includeBlackGoatCards: boolean = true,
    includeInsmouthCards: boolean = true,
    includeLurkerCards: boolean = true,
    includeMiskatonicCards: boolean = true,
    enableDunwichBoard: boolean = true,
    enableKingsportBoard: boolean = true,
    enableInnsmouthBoard: boolean = true
  ): Promise<WikiDeckResponse> {
    const params = new URLSearchParams();
    params.append('type', type);
    params.append('language', language);
    params.append('enableDunwichBoard', String(enableDunwichBoard));
    params.append('enableKingsportBoard', String(enableKingsportBoard));
    params.append('enableInnsmouthBoard', String(enableInnsmouthBoard));
    params.append('includeBaseCards', String(includeBaseCards));
    params.append('includeDunwichCards', String(includeDunwichCards));
    params.append('includeDarkPharaohCards', String(includeDarkPharaohCards));
    params.append('includeKingsportCards', String(includeKingsportCards));
    params.append('includeKingInYellowCards', String(includeKingInYellowCards));
    params.append('includeBlackGoatCards', String(includeBlackGoatCards));
    params.append('includeMiskatonicCards', String(includeMiskatonicCards));
    params.append('includeLurkerCards', String(includeLurkerCards));
    params.append('includeInsmouthCards', String(includeInsmouthCards));
    params.append('includeFanMadeInvestigators', String(includeFanMadeInvestigators));
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/wiki/deck?${params.toString()}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to load wiki deck');
    }
    return response.json();
  },

  async adminGrant(gameId: string, request: AdminGrantRequest): Promise<{ message: string; gameId: string }> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/admin/games/${encodeURIComponent(gameId)}/grant`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new AdminGrantError(error.message || 'Failed to grant', response.status);
    }
    return response.json();
  },

  async listUsers(): Promise<UserInfo[]> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/users`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to load users');
    }
    const data = await response.json();
    return data.users ?? [];
  },

  async updateUserRole(userId: string, role: UserRole): Promise<void> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/users/${encodeURIComponent(userId)}/role?role=${role}`, {
      method: 'PATCH',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update user role');
    }
  },

  async adminLoginAs(userName: string): Promise<AuthResponse> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/admin/auth/login-as`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userName }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to login as user');
    }
    return response.json();
  },

  async resetV2GameStateCache(): Promise<void> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/admin/cache/v2/reset`, {
      method: 'POST',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to reset v2 game state cache');
    }
  },

  async getStatePerformanceStatistics(): Promise<StatePerformanceSnapshot> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/admin/performance/state`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to load state performance statistics');
    }
    return response.json();
  },

  async adminUndoActions(gameId: string, actionsToUndo: number): Promise<AdminUndoActionsResponse> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/admin/game/undo-actions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gameId, actionsToUndo }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to undo actions');
    }
    return response.json();
  },

  async adminClearNotificationMessageIds(gameId: string): Promise<AdminClearNotificationMessageIdsResponse> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/admin/games/${encodeURIComponent(gameId)}/clear-notification-message-ids`, {
      method: 'POST',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to clear notification message ids');
    }
    return response.json();
  },

  async getAdminGameFullState(gameId: string): Promise<AdminGameFullStateResponse> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/admin/games/${encodeURIComponent(gameId)}/full-state`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to load game full state');
    }
    return response.json();
  },

  async updateAdminGameFullState(gameId: string, fullStateJson: string): Promise<AdminUpdateGameFullStateResponse> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/admin/games/${encodeURIComponent(gameId)}/full-state`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fullStateJson }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update game full state');
    }
    return response.json();
  },

  async exportDatabase(): Promise<AdminDatabaseExportPayload> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/admin/database/export`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to export database');
    }
    return response.json();
  },

  async importDatabase(payload: AdminDatabaseExportPayload): Promise<AdminDatabaseImportResponse> {
    const response = await gameApi.authorizedFetch(`${API_BASE_URL}/admin/database/import`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to import database');
    }
    return response.json();
  },

  async deleteGame(gameId: string, playerName: string): Promise<void> {
    const url = `${API_BASE_URL}/games/${encodeURIComponent(gameId)}?playerName=${encodeURIComponent(playerName)}`;
    const response = await gameApi.authorizedFetch(url, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete game');
    }
  },

  async adminDeleteGame(gameId: string): Promise<void> {
    const url = `${API_BASE_URL}/admin/games/${encodeURIComponent(gameId)}`;
    const response = await gameApi.authorizedFetch(url, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete game');
    }
  },
};
